import React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Chip, ChipVariants, Text, useTooltip } from '@frontend/design-system';

interface LocationTrialDetail {
  id: string;
  name: string;
  daysRemaining: number;
}

interface DaysRemainingChipProps {
  daysRemaining: number;
  locations: LocationTrialDetail[];
  hasVaryingDaysRemaining: boolean;
}

const getChipVariant = (
  daysRemaining: number
): { backgroundColor: string; textColor: string; chipVariant: ChipVariants } => {
  if (daysRemaining <= 1) {
    return {
      backgroundColor: 'var(--fabric-color-status-error-subtle)',
      textColor: 'var(--fabric-color-status-error-strong)',
      chipVariant: 'critical',
    };
  }
  if (daysRemaining <= 7) {
    return {
      backgroundColor: 'var(--fabric-color-status-warning-subtle)',
      textColor: 'var(--fabric-color-status-warning-strong)',
      chipVariant: 'warn',
    };
  }
  return {
    backgroundColor: 'var(--fabric-color-accent-seaweed-alpha-subtle)',
    textColor: 'var(--fabric-color-accent-seaweed-strong)',
    chipVariant: 'seaweed',
  };
};

export const DaysRemainingChip: React.FC<DaysRemainingChipProps> = ({
  daysRemaining,
  locations,
  hasVaryingDaysRemaining,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'bottom-start',
  });

  const sortedLocations = React.useMemo(() => {
    return [...locations].sort((a, b) => a.daysRemaining - b.daysRemaining);
  }, [locations]);

  const chipVariantConfig = getChipVariant(daysRemaining);

  return (
    <>
      <Chip
        variant={chipVariantConfig.chipVariant}
        css={[styles.chip, { backgroundColor: chipVariantConfig.backgroundColor, color: chipVariantConfig.textColor }]}
        rightElement={
          hasVaryingDaysRemaining ? (
            <div {...triggerProps} css={styles.infoIconWrapper}>
              <Icon name='info-small' />
            </div>
          ) : undefined
        }
      >
        {t('{{count}} Days Remaining', { count: daysRemaining })}
      </Chip>

      <Tooltip {...tooltipProps} css={styles.tooltip}>
        <ul css={styles.tooltipList}>
          {sortedLocations.map((loc) => (
            <li key={loc.id} css={styles.tooltipItem}>
              <Text as='span' size='medium' color='white'>
                {loc.name}:
              </Text>
              <Text as='span' size='medium' color='white' weight='bold'>
                {t('{{count}} Days Remaining', { count: loc.daysRemaining })}
              </Text>
            </li>
          ))}
        </ul>
      </Tooltip>
    </>
  );
};

const styles = {
  chip: css`
    gap: var(--fabric-space-gap-xxs);
    max-width: 100%;

    span {
      font-weight: var(--fabric-typography-metric-md-font-weight);
    }
  `,
  infoIconWrapper: css`
    display: flex;
    align-items: center;
    cursor: pointer;
  `,
  tooltip: css`
    max-width: 420px;
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
  `,
  tooltipList: css`
    margin: 0;
    padding-left: var(--fabric-space-gap-sm);
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xxs);
  `,
  tooltipItem: css`
    display: flex;
    gap: var(--fabric-space-gap-xs);
    justify-content: space-between;
  `,
};
